import React, { useCallback, useMemo, useState } from 'react';
import { Download, RefreshCw, Upload, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface Props {
  lists: List[];
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_EMAILS = 50_000;

function listLabel(l: List): string {
  return l.name ?? l.listname ?? `List ${l.listid}`;
}

/** Client-side preview parse: unique emails + duplicate count. */
function parseEmailsFromCsvText(text: string): { emails: string[]; duplicates: number } {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { emails: [], duplicates: 0 };

  let emailCol = 0;
  let start = 0;
  const firstCells = lines[0].split(',').map((c) => c.replace(/^"|"$/g, '').trim());
  const headerIdx = firstCells.findIndex((c) => c.toLowerCase().includes('email'));
  if (headerIdx >= 0) {
    emailCol = headerIdx;
    start = 1;
  }

  const seen = new Set<string>();
  const emails: string[] = [];
  let duplicates = 0;
  for (let i = start; i < lines.length; i++) {
    const cells = lines[i].split(',').map((c) => c.replace(/^"|"$/g, '').trim());
    const email = (cells[emailCol] ?? cells[0] ?? '').trim();
    if (!email.includes('@')) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) {
      duplicates++;
      continue;
    }
    seen.add(key);
    emails.push(email);
  }
  return { emails, duplicates };
}

export default function CsvHydrate({ lists }: Props) {
  const [listId, setListId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [previewEmails, setPreviewEmails] = useState<string[]>([]);
  const [previewDupes, setPreviewDupes] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    input: number;
    matched: number;
    notFound: number;
    duplicates: number;
  } | null>(null);

  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => listLabel(a).localeCompare(listLabel(b))),
    [lists]
  );

  const onFile = useCallback(async (f: File | null) => {
    setFile(f);
    setSummary(null);
    setError(null);
    setParseError(null);
    setPreviewEmails([]);
    setPreviewDupes(0);
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setParseError('File exceeds 5 MB limit.');
      setFile(null);
      return;
    }
    try {
      const text = await f.text();
      const { emails, duplicates } = parseEmailsFromCsvText(text);
      if (!emails.length) {
        setParseError('No email addresses found in this file.');
        setFile(null);
        return;
      }
      if (emails.length > MAX_EMAILS) {
        setParseError(`Too many emails (${emails.length.toLocaleString()}). Max is ${MAX_EMAILS.toLocaleString()}.`);
        setFile(null);
        return;
      }
      setPreviewEmails(emails);
      setPreviewDupes(duplicates);
    } catch {
      setParseError('Could not read CSV file.');
      setFile(null);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const handleHydrate = async () => {
    if (listId === '' || !file) {
      setError('Select a list and upload a CSV first.');
      return;
    }
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('listId', String(listId));
      const res = await axios.post('/api/interspire/utilities/csv-hydrate', form, {
        responseType: 'blob',
        timeout: 0,
      });

      const input = parseInt(res.headers['x-hydrate-input'] ?? '0', 10) || previewEmails.length;
      const matched = parseInt(res.headers['x-hydrate-matched'] ?? '0', 10) || 0;
      const notFound = parseInt(res.headers['x-hydrate-not-found'] ?? '0', 10) || 0;
      const duplicates = parseInt(res.headers['x-hydrate-duplicates'] ?? '0', 10) || previewDupes;
      setSummary({ input, matched, notFound, duplicates });

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hydrated_list_${listId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e: any) {
      // Blob error responses need text parse
      let msg = 'Hydrate failed. Please try again.';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } catch { /* keep default */ }
      } else if (e?.response?.data?.error) {
        msg = e.response.data.error;
      } else if (e?.message) {
        msg = e.message;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const canRun = listId !== '' && !!file && previewEmails.length > 0 && !loading;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 space-y-5">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-blue-500" />
            CSV Hydrate
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Upload a CSV of email addresses, pick one Interspire list, and download a hydrated file with
            name, County/State, subscribe/unsubscribe dates, status, and list ID. Unmatched emails are
            kept with status <code className="text-xs">not_found</code>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Source list <span className="text-red-500">*</span>
            </label>
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-2"
            >
              <option value="">Select a list…</option>
              {sortedLists.map((l) => (
                <option key={l.listid} value={l.listid}>
                  {listLabel(l)} (ID {l.listid})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              CSV file <span className="text-red-500">*</span>
            </label>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg px-4 py-6 text-center hover:border-blue-400 transition-colors"
            >
              <Upload className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 dark:text-slate-300 mb-2">
                {file ? file.name : 'Drop a CSV here, or choose a file'}
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                className="text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-xs text-gray-400 mt-2">Max 50,000 emails · 5 MB</p>
            </div>
          </div>
        </div>

        {parseError && (
          <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {parseError}
          </div>
        )}

        {previewEmails.length > 0 && (
          <div className="rounded-md bg-slate-50 dark:bg-slate-900/50 border border-gray-200 dark:border-slate-700 px-4 py-3 text-sm text-gray-700 dark:text-slate-300">
            <strong>{previewEmails.length.toLocaleString()}</strong> unique email
            {previewEmails.length !== 1 ? 's' : ''} ready
            {previewDupes > 0 && (
              <span className="text-gray-500 dark:text-slate-400">
                {' '}
                · {previewDupes.toLocaleString()} duplicate{previewDupes !== 1 ? 's' : ''} removed
              </span>
            )}
            <div className="mt-1 text-xs text-gray-500 dark:text-slate-400 truncate">
              Sample: {previewEmails.slice(0, 3).join(', ')}
              {previewEmails.length > 3 ? '…' : ''}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleHydrate}
            disabled={!canRun}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {loading ? 'Hydrating…' : 'Hydrate & Download'}
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Unique input', value: summary.input },
              { label: 'Matched', value: summary.matched },
              { label: 'Not found', value: summary.notFound },
              { label: 'Dupes removed', value: summary.duplicates },
            ].map((c) => (
              <div
                key={c.label}
                className="rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2"
              >
                <div className="text-xs text-gray-500 dark:text-slate-400">{c.label}</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {c.value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
